import {useCallback, useMemo} from "react"
import {ApolloLoad} from "./hookShared"

export interface UseQueriesResult<Data> extends Base {
  data: Data | undefined
}

interface Base {
  error?: string
  load: ApolloLoad
  loading: boolean
  startPolling?: (pollingTime: number) => void
  stopPolling?: () => void
  refetch?: () => Promise<any>
}

type InferData<A> = A extends {data: infer Data} ? Data : never

interface AggregatedAll<A> extends Base {
  data?: A[]
}

interface Aggregated<A> extends Base {
  data?: [A]
}
interface AggregatedB<A, B> extends Base {
  data?: [A, B]
}
interface AggregatedC<A, B, C> extends Base {
  data?: [A, B, C]
}
interface AggregatedD<A, B, C, D> extends Base {
  data?: [A, B, C, D]
}
interface AggregatedE<A, B, C, D, E> extends Base {
  data?: [A, B, C, D, E]
}
interface AggregatedF<A, B, C, D, E, F> extends Base {
  data?: [A, B, C, D, E, F]
}

/**
 * Combine a list of useQuery hooks into a single object.
 *
 * @example
 * ```tsx
 * const MyComp = ({uuid}) => {
 *   const { load, data } = useQuerys(
 *     useUserQuery(uuid),
 *     useBusinessQuery(uuid),
 *   )
 *
 *   const [userData, businessData] = data
 *
 *   return (
 *     <Loading load={load}>
 *       <p>Hello {userData.name}!</p>
 *       <p>Member of {businessData.name}</p>
 *     </Loading>
 *   )
 * }
 * ```
 *
 * NOTE: I'm not convinced this is memod properly
 */
export function useQuerys<A extends Base>(hook: A): Aggregated<InferData<A>>
export function useQuerys<A extends Base, B extends Base>(hook: A, hookB: B): AggregatedB<InferData<A>, InferData<B>>
export function useQuerys<A extends Base, B extends Base, C extends Base>(
  hook: A,
  hookB: B,
  hookC: C
): AggregatedC<InferData<A>, InferData<B>, InferData<C>>
export function useQuerys<A extends Base, B extends Base, C extends Base, D extends Base>(
  hook: A,
  hookB: B,
  hookC: C,
  hookD: D
): AggregatedD<InferData<A>, InferData<B>, InferData<C>, InferData<D>>
export function useQuerys<A extends Base, B extends Base, C extends Base, D extends Base, E extends Base>(
  hook: A,
  hookB: B,
  hookC: C,
  hookD: D,
  hookE: E
): AggregatedE<InferData<A>, InferData<B>, InferData<C>, InferData<D>, InferData<E>>
export function useQuerys<A extends Base, B extends Base, C extends Base, D extends Base, E extends Base, F extends Base>(
  hook: A,
  hookB: B,
  hookC: C,
  hookD: D,
  hookE: E,
  hookF: F
): AggregatedF<InferData<A>, InferData<B>, InferData<C>, InferData<D>, InferData<E>, InferData<F>>
export function useQuerys<A extends Base>(...hooks: A[]): AggregatedAll<A> {
  // Derive primitive flags first
  const loading = hooks.some((h) => h.loading)
  const error = hooks.find((h) => h.error)?.error

  // Stable aggregated refetch
  const refetchAll = useCallback(
    () => Promise.all(hooks.map((h) => h.refetch?.())),
    // refetch functions themselves (stable if underlying hooks memoize them)
    hooks.map((h) => h.refetch)
  )

  // Memoize derived arrays so consumers relying on referential equality don't re-render
  const load = useMemo(
    () => hooks.map((h) => h.load) as unknown as ApolloLoad,
    hooks.map((h) => h.load)
  )

  const data = useMemo(
    () => hooks.map((h) => (h as any).data),
    hooks.map((h) => (h as any).data)
  )

  return {
    loading,
    error,
    load,
    data,
    refetch: refetchAll
  }
}
